import { cloneDeep } from 'lodash';
import { Point } from './point';
import { Segment } from './curve';
import { bezierBounds } from './bounds'; // [TODO] Bring bezierBounds here


// From SCIS_Figures\bezier_utils.cs

const BezierVectors = function(a,b,c,d,e,f) {
  const _this = this;
  _this.a = a;
  _this.b = b;
  _this.c = c;
  _this.d = d;
  _this.e = e;
  _this.f = f;
};
BezierVectors.prototype = {
  constructor: BezierVectors,
  eval( u ) {
    const a = this.a, b = this.b, c = this.c, d = this.d;
    return new Point( ( ( a.x * u + b.x ) * u + c.x ) * u + d.x ,
                   ( ( a.y * u + b.y ) * u + c.y ) * u + d.y );
  },
  evalTangent( u ) {
    const _1u = 1-u, u2 = u*u, u_1u = u*_1u, _1u2 = _1u*_1u, c = this.c, e = this.e, f = this.f;
    return new Point( c.x * _1u2 + e.x * u_1u + f.x * u2 ,
                   c.y * _1u2 + e.y * u_1u + f.y * u2 );
  },
};

// For the moment, we do not autogenerate bounds, midPoint and squaredHeight
const BezierCache = function(vectors,startingTangent,endingTangent,pApprox) {
  this.vectors         = vectors;
  this.startingTangent = startingTangent;
  this.endingTangent   = endingTangent;
  this.pApprox         = pApprox;
};

const BezierPolygonalApproximation = function(points,l,u,arcLen) {
  this.points = points;
  this.l      = l;
  this.u      = u;
  this.arcLen = arcLen;
};

BezierPolygonalApproximation.prototype = {
  clone() {
    return new BezierPolygonalApproximation(cloneDeep(this.points),cloneDeep(this.l),cloneDeep(this.u),this.arcLen);
  }
};

// Generates a polyline approximation of the bezier curve aBez.
// points contains the polyline vertices.
// l contains the arc-len of each vertex.
// Returns the length of the approximating polyline, which represents
// the approximated length of the bezier curve.

const ApproximatingPolyBuilder = function(piece,flatness,minPoints,useControlPointsForFlatnessTest) {
  this.f2 = flatness*flatness;
  const r = this.result = new BezierPolygonalApproximation([],[],[],0);
  r.points.push(piece.P0.clone());
  r.l.push(0);
  r.u.push(0);
  this.build(piece,0,1,2*(minPoints-1),useControlPointsForFlatnessTest);
};
ApproximatingPolyBuilder.prototype = {
  constructor: ApproximatingPolyBuilder,
  // Recursion of the function that generates a polyline approximation of the bezier curve aBez.
  // aL is the accumulated length of the left recursion branch (used to carry the result)
  // [aU0,aU1] is the interval of polynomial parameters representing the current subcurve.
  build(  piece, u0, u1, fmp, useControlPointsForFlatnessTest ) {
    if( fmp > 2 || ( (u1 - u0) > 1e-4 && !piece.isFlatEnough2(this.f2,useControlPointsForFlatnessTest) ) ) {
      const mu = (u0 + u1) / 2;
      const ps = piece.split(0.5);
      this.build(ps.left , u0, mu, fmp/2, useControlPointsForFlatnessTest);
      this.build(ps.right, mu, u1, fmp/2, useControlPointsForFlatnessTest);
    }
    else {
      const result = this.result, piece_last = piece.last();
      result.points.push(piece_last);
      const arcLen = Point.distance(piece.P0, piece_last);
      result.arcLen += arcLen;
      result.l.push(result.arcLen);
      result.u.push(u1);
    }
  }
};

const BezierPolygonalApproximation_generateForInternalRepresentation = function(piece) {
  const builder = new ApproximatingPolyBuilder(piece, 0.01, 2, true);
  return builder.result;
};

// Direct polygonizer, just get the points, do not save arc len metrics
const BezierPolygonizer = function( piece, flatness, minPoints ) {
  this.f2 = flatness * flatness;
  this.polygon = [ piece.P0.clone() ];
  this.build(piece,0,1,2*(minPoints-1));
};
BezierPolygonizer.prototype = {
  constructor: BezierPolygonizer,
  build(  piece, u0, u1, fmp ) {
    if( fmp > 2 || ( (u1 - u0) > 1e-4 && !piece.isFlatEnough2(this.f2) ) ) {
      const uh = (u0 + u1) / 2;
      const pieces = piece.split(0.5);
      this.build(pieces.left , u0, uh, fmp/2);
      this.build(pieces.right, uh, u1, fmp/2);
    }
    else {
      this.polygon.push(piece.last());
    }
  }
};

const binarySearchLowerBound = function( a, value ) {
  const { floor } = Math;
  let lo = 0, hi = a.length - 1, mid;
  while (lo <= hi) {
    mid = floor((lo+hi)/2);
    if(a[mid] > value) {
      hi = mid - 1;
    }
    else if(a[mid] < value) {
      lo = mid + 1;
    }
    else {
      return mid;
    }
  }
  return lo;
};

// Cubic
//------------------------------------------------------------------------------------

export const Cubic = function( P0, P1, P2, P3 ) {
  this.P0 = P0;
  this.P1 = P1;
  this.P2 = P2;
  this.P3 = P3;
  this._c = null; // Cache
};
Cubic.prototype = {
  constructor: Cubic,
  type: 'Cubic',

  clone() {
    return new Cubic(this.P0.clone(), this.P1.clone(), this.P2.clone(), this.P3.clone());
  },

  isEqual( other ) {
    return other instanceof Cubic &&
           this.P0.isEqual(other.P0) &&
           this.P1.isEqual(other.P1) &&
           this.P2.isEqual(other.P2) &&
           this.P3.isEqual(other.P3);
  },

  last( v ) {
    return v===undefined ? this.P3 : (this.P3=v,this);
  },

  get bounds() {
    return bezierBounds(this.P0, this.P1, this.P2, this.P3);
  },

  boundsAfter( t ) {
    return bezierBounds(t.apply(this.P0), t.apply(this.P1), t.apply(this.P2), t.apply(this.P3));
  },

  chainedTransform( op ) {
    this.P1.transform(op);
    this.P2.transform(op);
    this.P3.transform(op);
    this._c = null;
  },

  chainedDraw( context, transform ) {
    const tP1 = transform ? transform.apply(this.P1) : this.P1,
        tP2 = transform ? transform.apply(this.P2) : this.P2,
        tP3 = transform ? transform.apply(this.P3) : this.P3;
    context.bezierCurveTo(tP1.x,tP1.y, tP2.x,tP2.y, tP3.x,tP3.y);
  },

  chainedToSVG() {
    return " C" + this.P1.toSVG() +
            " " + this.P2.toSVG() +
            " " + this.P3.toSVG();
  },

  chainedToXmlString() {
    return this.P0.toXmlString() + ',' + this.P1.toXmlString() + ',' + this.P2.toXmlString();
  },

  chainedPoints( points ) {
    points.push(this.P0);
    points.push(this.P1);
    points.push(this.P2);
  },

  toXmlString() {
    return this.chainedToXmlString() + ',' + this.P3.toXmlString();
  },

  draw( context, transform ) {
    const tP0 = transform ? transform.apply(this.P0) : this.P0;
    context.moveTo(tP0.x,tP0.y);
    this.chainedDraw(context,transform);
  },

  eval( t ) {
    return Point.FromBezier(t,this.P0,this.P1,this.P2,this.P3);
  },

  split( u ) {
    const mu = 1-u;

    const P01 = Point.sm(this.P0,mu).add( Point.sm(this.P1,u) );
    const P12 = Point.sm(this.P1,mu).add( Point.sm(this.P2,u) );
    const P23 = Point.sm(this.P2,mu).add( Point.sm(this.P3,u) );

    const P012 = Point.sm(P01,mu).add( Point.sm(P12,u) );
    const P123 = Point.sm(P12,mu).add( Point.sm(P23,u) );

    const PC = Point.sm(P012,mu).add( Point.sm(P123,u) );

    return {
      left : new Cubic( this.P0.clone(), P01 , P012, PC              ),
      right: new Cubic( PC.clone()     , P123, P23 , this.P3.clone() )
    };
  },

  isFlatEnough2( threshold2 ) {
    const e1 = this.P1.distanceToLine2( this.P0, this.P3 );
    const e2 = this.P2.distanceToLine2( this.P0, this.P3 );
    return ( e1>e2 ? e1 : e2 ) < threshold2;
  },

  // This functions triggers caching

  arcLenToTime( x ) {
    const arcLen = this.arcLength();

    if( arcLen >= 1e-15 ) {
      if( x <= 0 || x >= arcLen ) {
        return x / arcLen;
      }
      else {
        // Report issue: https://github.com/Stahls/cadx/blob/master/SCIS_Figures/cubic_bezier_piece.cs#L301
        const pApprox = this.pApprox();
        const l = pApprox.l, u = pApprox.u;
        const idx = binarySearchLowerBound(l,x);

        const l_idx_1 = l[idx-1], l_idx = l[idx];
        if( idx < l.length && l_idx_1 < x && x <= l_idx && l_idx_1 < l_idx ) {
          const ratio = ( x - l_idx_1 ) / ( l_idx - l_idx_1 );
          return u[idx] * ratio + u[idx-1] * (1 - ratio);
        }
        else {
          return x / arcLen ;
        }
      }
    }
    return 0;
  },

  at( x ) {
    return this.vectors().eval( this.arcLenToTime(x) );
  },

  extendedAt( x ) { // [TODO] Remove dependency to Segment
    if( x <= 0 ) {
      return new Segment(this.P0, this.P1).extendedAt(x);
    }
    else {
      const arcLen = this.arcLength();
      if( x >= arcLen ) {
        return new Segment(this.P3, Point.add( this.P3, this.P3.minus(this.P2) )).extendedAt( x - arcLen );
      }
      else {
        return this.at(x);
      }
    }
  },

  tangentAt( x ) {
    return this.vectors().evalTangent( this.arcLenToTime(x) );
  },

  vectors() {
    if( ! this._c ) {
      this.createCache();
    }
    return this._c.vectors;
  },

  arcLength() {
    if( ! this._c ) {
      this.createCache();
    }
    return this._c.pApprox.arcLen;
  },

  pApprox() {
    if( ! this._c ) {
      this.createCache();
    }
    return this._c.pApprox;
  },

  polygonize( flatness, minPoints ) {
    return ( new BezierPolygonizer(this,flatness,minPoints) ).polygon;
  },

  createCache() {
    const P0 = this.P0, P1 = this.P1, P2 = this.P2, P3 = this.P3;
    const f = P3.minus(P2).sm( 3 );
    const e = P2.minus(P1).sm( 6 );
    const d = P0.clone();
    const c = P1.minus(P0).sm( 3 );
    const b = P2.minus(P1).sm( 3 ).substract(c);
    const a = P3.minus(P0).substract(c).substract(b);

    const vectors = new BezierVectors(a,b,c,d,e,f);

    const startingTangent = vectors.evalTangent(0);
    const endingTangent   = vectors.evalTangent(1);

    const pApprox = BezierPolygonalApproximation_generateForInternalRepresentation(this);

    this._c = new BezierCache(vectors,startingTangent,endingTangent,pApprox);
  },

  reversed() {
    return new Cubic(this.P3.clone(),this.P2.clone(),this.P1.clone(),this.P0.clone());
  }
};

Cubic.fromSegment = function(P0, P3) {
  const P1 = Point.sm( Point.sm(P0, 2) .plus(P3), 1 / 3);
  const P2 = Point.sm( P0 .plus( Point.sm(P3, 2) ), 1 / 3);
  return new Cubic(P0, P1, P2, P3);
};


//CP0 = QP0
//CP3 = QP2
//CP1 = QP0 + 2/3 *(QP1-QP0)
//CP2 = QP2 + 2/3 *(QP1-QP2)
Cubic.fromQuadraticPoints = function( P0, P12, P3 ) {
  const P12_2 = Point.sm(P12, 2);
  const P1 = ( P0 .plus( P12_2 ) ).sm(1 / 3);
  const P2 = ( P12_2 .plus( P3 ) ).sm(1 / 3);
  return new Cubic(P0, P1, P2, P3);
};



/** WEBPACK FOOTER **
 ** ./src/cdl/geometry/bezier.js
 **/